import { useState } from 'react';
import usePartialDataLoader from 'shared/data/usePartialDataLoader';
import { CheckableLine, NoPricingLine } from './ProductPricingLines';
import {
	Availability,
	getInitialProductDetailsState,
	ProductDetailsState,
	ProductPartialDataLoaderOptions,
	ProductPricingLine,
	ProductType,
	ReviewSummaryDto,
	TabViewChunk,
} from './types';

export interface UseProductDetails {
	state: UseProductDetailsState;
	actor: UseProductDetailsActor;
}

/**
 * Use Product Details Context State
 */
export interface UseProductDetailsState {
	loggedInUserFirstName: string;
	productId: string;
	groupId: string;
	reviewsSummary: ReviewSummaryDto;
	simpleProductLoading: boolean;
	hasError: boolean;
	productDetails: ProductDetailsState;
	productStateOverride: Partial<ProductDetailsState> | null;
	userIsSignedIn: boolean;
	productQuantity: number | null;
	selectedPricingLine: CheckableLine;
}

/**
 * Use Product Details Context Actor
 */
export interface UseProductDetailsActor {
	setLoggedInUserFirstName: (name: string) => void;
	setSimpleProductLoading: (prop: boolean) => void;
	setProductStateOverride: (data: Partial<ProductDetailsState> | null) => void;
	isLoadingAny: (keys: Array<keyof ProductDetailsState>) => boolean;
	setHasError: (hasError: boolean) => void;
	setUserIsSignedIn: (isSignIn: boolean) => void;
	setProductQuantity: (qty: number | null) => void;
	setSelectedPricingLine: (CheckableLine: CheckableLine) => void;
	isPricingLineUnavailable: () => boolean;
	isPurchaseable: (availability?: Availability | null, pricingLine?: ProductPricingLine) => boolean;
}

export interface UseProductDetailsProps {
	loaderOptions: ProductPartialDataLoaderOptions;
}

/**
 * Reusable hook to access product detail related endpoints on the server, as well as helping manage
 * product detail information.
 */
export default function useProductDetails(props: UseProductDetailsProps): UseProductDetails {
	const { loaderOptions } = props;

	// to indicate that a simple product details are being loaded after selections are made
	const [simpleProductLoading, setSimpleProductLoading] = useState(false);
	const [loggedInUserFirstName, setLoggedInUserFirstName] = useState('');

	// defines state that can be used to override the existing state defined by initial state + partial loader
	// this is primarily used for group products, where a simple product takes over after a selection is made
	const [productStateOverride, setInternalProductStateOverride] = useState<Partial<ProductDetailsState> | null>(null);

	const [userIsSignedIn, setUserIsSignedIn] = useState(false);
	const [productQuantity, setProductQuantity] = useState<number | null>(1);

	const [selectedPricingLine, setSelectedPricingLine] = useState<CheckableLine>(NoPricingLine);

	const setProductStateOverride = (data: Partial<ProductDetailsState> | null) => {
		if (data == null) {
			setInternalProductStateOverride(null);
			return;
		}

		const originalState = getInitialProductDetailsState();

		const foldTabs = () => {
			const newTabs = data.tabs != null ? [...data.tabs] : [];
			const newTabsMap = newTabs.reduce(function (map: { [index: string]: TabViewChunk }, obj) {
				map[obj.tab.id] = obj;
				return map;
			}, {});

			for (const oldTab of originalState?.tabs ?? []) {
				if (!newTabsMap[oldTab.tab.id]) {
					newTabs.push(oldTab);
				}
			}

			return newTabs;
		};

		const fixedNewState: Partial<ProductDetailsState> = {
			...data,
			tabs: foldTabs(),
		};

		setInternalProductStateOverride(fixedNewState);
	};

	// this will grab the details from the server for you, and get you the state necessary
	// normally, we would just have all of the missing fields here, but sometimes, if we have multiple
	// integration calls, it may be good to be able to split this up. in this case, this is an example
	// of splitting up pricing/images and availability into separate calls.
	// this would apply if you have user-specific pricing, and the availability service is
	// really slow. you'd want to show pricing right away, and take time before showing availability
	const productData = usePartialDataLoader<ProductDetailsState>({
		keys: [
			'summary',
			'displayPricing',
			'pricingLines',
			'assets',
			'availability',
			'brand',
			'tabs',
			'selections',
			'relatedProducts',
			'reviewsSummary',
			'breadcrumbPath',
			'reviewsSummary',
			'addOns',
		],
		...loaderOptions,
	});

	const { isLoadingAny, hasError, setHasError } = productData;

	const productDetails = { ...productData.partialData, ...productStateOverride };

	const groupId =
		productDetails.summary.type === ProductType.Group
			? productDetails.id
			: productDetails.summary.productGroupId || '';

	/** Returns true if the selected pricing line is unavailable and can't be purchased. */
	const isPricingLineUnavailable = () =>
		selectedPricingLine.pricingLine == null || selectedPricingLine.pricingLine?.unavailabilityReason != null;

	const isPurchaseable = (availability?: Availability | null, pricingLine?: ProductPricingLine) => {
		return availability?.outOfStock != true && pricingLine != null && pricingLine?.unavailabilityReason == null;
	};

	return {
		state: {
			loggedInUserFirstName,
			productId: productDetails.id,
			groupId,
			reviewsSummary: productDetails.reviewsSummary,
			simpleProductLoading,
			productDetails,
			productStateOverride,
			hasError,
			userIsSignedIn,
			productQuantity,
			selectedPricingLine,
		},
		actor: {
			setLoggedInUserFirstName,
			setSimpleProductLoading,
			isLoadingAny,
			setProductStateOverride,
			setHasError,
			setUserIsSignedIn,
			setProductQuantity,
			setSelectedPricingLine,
			isPricingLineUnavailable,
			isPurchaseable,
		},
	};
}
